import { WatchQueryFetchPolicy } from '@apollo/client';
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom';

import useUserData from 'approot/shared/user/user-data';
import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import type {
  GetDraftContentQuery,
  GetDraftContentQueryVariables,
} from './activity-builder-draft.graphql.generated';
import {
  GET_DRAFT_CONTENT_DRAFT,
  GET_DRAFT_CONTENT_SECTIONS,
} from './activity-builder-draft.graphql';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import type {
  GetDraftContentSectionsQuery,
  GetDraftContentSectionsQueryVariables,
} from './activity-builder-draft.graphql.generated';
import { getEditUrl } from 'approot/activity-builder/activity-builder-url.utils';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import {
  ExtraSectionIdentifiers,
  useActivityBuilderApollo,
} from '../activity-builder.apollo';
import { MutableRefObject, useEffect } from 'react';
import { scrollSlideIntoView } from 'approot/activity-builder/activity-builder-slides.utils';
import { useActivityBuilderTheme } from '../theme/activity-builder-theme';
import { ActivitiesFontSize } from 'global/constants';

export const useGetLessonDraft = ({
  contentDraftId,
}: {
  contentDraftId: string | null;
}) => {
  const { isSignedIn } = useUserData();
  const { showError } = useSnackbar();
  const navigate = useNavigate();
  const { slug, userClassId, contentType } = useGetInteractiveLessonParams();
  const { updateFontSize } = useActivityBuilderTheme();

  const { data, loading, error } = useLoggingQuery<
    GetDraftContentQuery,
    GetDraftContentQueryVariables
  >(
    GET_DRAFT_CONTENT_DRAFT,
    {
      skip: !contentDraftId,
      variables: {
        input: {
          contentDraftId: contentDraftId || '',
          contentType,
        },
      },
      onCompleted: res => {
        if (res.getDraftContent.styles) {
          updateFontSize(
            res.getDraftContent.styles.fontSize as ActivitiesFontSize
          );
        }
      },
      onError: () => {
        showError('That version cannot be found. Please try again.');
        navigate(getEditUrl({ slug, userClassId, contentType }), {
          replace: true,
        });
      },
      fetchPolicy: 'cache-and-network',
    },
    isSignedIn
  );

  return {
    data,
    loading,
    error,
  };
};

export const useGetDraftLessonSections = ({
  contentDraftId,
  sectionIds,
  fetchPolicy = 'cache-and-network',
  skip,
}: {
  contentDraftId: string | null;
  sectionIds?: string[];
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
}) => {
  const { isSignedIn } = useUserData();
  const { contentType } = useGetInteractiveLessonParams();

  const { data, loading, error } = useLoggingQuery<
    GetDraftContentSectionsQuery,
    GetDraftContentSectionsQueryVariables
  >(
    GET_DRAFT_CONTENT_SECTIONS,
    {
      skip: !contentDraftId || skip,
      variables: {
        input: {
          contentDraftId: contentDraftId || '',
          sectionsIds: sectionIds,
          contentType,
        },
      },
      fetchPolicy,
    },
    isSignedIn
  );

  return {
    data,
    loading,
    error,
  };
};

export const useUpdateDraftSectionUsingParams = ({
  sortedSections,
  isInitialisedRef,
  containerRef,
}: {
  sortedSections: { id: string }[];
  isInitialisedRef: MutableRefObject<boolean>;
  containerRef: HTMLElement | null;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('page');
  const draftId = searchParams.get('draftId');

  const { setActiveSection } = useActivityBuilderApollo();

  useEffect(() => {
    // scroll the current section slide into view
    if (containerRef && section) {
      scrollSlideIntoView({
        sectionId: `section-${section}`,
        containerRef,
      });
    }
  }, [containerRef, section]);

  useEffect(() => {
    if (section === ExtraSectionIdentifiers.Conclusion) {
      setActiveSection(section);
      return;
    }

    // initialise to a particular page on load
    if (sortedSections.length > 0 && !isInitialisedRef.current) {
      if (section) {
        if (sortedSections.find(s => s.id === section)) {
          setActiveSection(section);
        } else {
          // invalid section id
          setActiveSection(ExtraSectionIdentifiers.Introduction);
          const urlWithoutParams = location.pathname;
          if (draftId) {
            navigate(
              `${urlWithoutParams}?page=${ExtraSectionIdentifiers.Introduction}&draftId=${draftId}`
            );
          } else {
            navigate(
              `${urlWithoutParams}?page=${ExtraSectionIdentifiers.Introduction}`
            );
          }
        }
      } else {
        setActiveSection(ExtraSectionIdentifiers.Introduction);
      }
      isInitialisedRef.current = true;
    }
    // eslint-disable-next-line
  }, [sortedSections, section]);

  useEffect(() => {
    // listen for any changes to params (e.g. click browser back button)
    if (navigationType === 'POP') {
      if (section === ExtraSectionIdentifiers.Conclusion) {
        setActiveSection(section);
        return;
      }

      if (section && sortedSections.length > 0) {
        if (sortedSections.find(s => s.id === section)) {
          setActiveSection(section);
        } else {
          // invalid section id
          const urlWithoutParams = location.pathname;
          if (draftId) {
            navigate(`${urlWithoutParams}?draftId=${draftId}`);
          } else {
            navigate(urlWithoutParams);
          }
          setActiveSection(ExtraSectionIdentifiers.Introduction);
        }
      } else {
        setActiveSection(ExtraSectionIdentifiers.Introduction);
      }
    }
    // eslint-disable-next-line
  }, [navigationType, navigate, section]);
};
